import React, {ReactElement, useEffect, useRef, useState} from 'react';
import {Icon} from '@iconify/react';
import {createPortal} from 'react-dom';
import {useOptionalPresScaleContext} from '../../../../context/PresScaleContext';

interface AnswerCommentProps {
    comment: string;
    showComment: boolean;
    setShowComment?: (show: boolean) => void;
    anchorRef: React.RefObject<HTMLDivElement | null>;
}

function AnswerComment({comment, showComment, setShowComment, anchorRef}: AnswerCommentProps): ReactElement {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const [dropdownPlacement, setDropdownPlacement] = useState<'top' | 'bottom'>('bottom');
    const [portalRoot, setPortalRoot] = useState<HTMLElement>(document.body);
    const [hoverOpened, setHoverOpened] = useState(false);
    const useFixedPosition = portalRoot === document.body;
    const canvasScale = useOptionalPresScaleContext()?.scale;
    const scale = canvasScale || 1;

    useEffect(() => {
        const ownerDocument = anchorRef.current?.ownerDocument;
        if (ownerDocument && ownerDocument !== document && ownerDocument.body) {
            setPortalRoot(ownerDocument.body);
            return;
        }

        const localPortalHost =
            anchorRef.current?.closest('[data-answer-comment-portal]') || anchorRef.current?.closest('#slide-workspace');
        if (localPortalHost instanceof HTMLElement) {
            setPortalRoot(localPortalHost);
        } else {
            setPortalRoot(document.body);
        }
    }, [anchorRef]);

    useEffect(() => {
        if (!showComment || !anchorRef.current) {
            return (): void => undefined;
        }

        const updateDropdownPosition = (): void => {
            const rect = anchorRef.current?.getBoundingClientRect();
            if (!rect) return;

            if (useFixedPosition) {
                const spaceAbove = rect.top;
                const spaceBelow = window.innerHeight - rect.bottom;
                const dropdownHeight = dropdownRef.current?.offsetHeight ?? 0;
                const shouldOpenTop = dropdownHeight > spaceBelow && spaceAbove > spaceBelow;
                const maxHeight = Math.max((shouldOpenTop ? spaceAbove : spaceBelow) - 8, 80);

                setDropdownPlacement(shouldOpenTop ? 'top' : 'bottom');
                setDropdownStyle({
                    position: 'fixed',
                    top: shouldOpenTop ? 'auto' : rect.bottom - 3,
                    bottom: shouldOpenTop ? window.innerHeight - rect.top - 3 : 'auto',
                    left: rect.left,
                    width: rect.width,
                    maxHeight,
                    zIndex: 1100,
                });
                return;
            }

            const slideRect = portalRoot.getBoundingClientRect();

            const scaleX = portalRoot.offsetWidth ? slideRect.width / portalRoot.offsetWidth : 1;
            const scaleY = portalRoot.offsetHeight ? slideRect.height / portalRoot.offsetHeight : 1;

            const dropdownHeight = dropdownRef.current?.offsetHeight ?? 0;
            const spaceAbove = (rect.top - slideRect.top) / scaleY;
            const spaceBelow = (slideRect.bottom - rect.bottom) / scaleY;
            const shouldOpenTop = dropdownHeight > spaceBelow && spaceAbove > spaceBelow;
            const maxHeight = Math.max((shouldOpenTop ? spaceAbove : spaceBelow) - 8, 80);

            setDropdownPlacement(shouldOpenTop ? 'top' : 'bottom');

            setDropdownStyle({
                position: 'absolute',
                top: shouldOpenTop ? 'auto' : (rect.bottom - slideRect.top) / scaleY - 3,
                bottom: shouldOpenTop ? (slideRect.bottom - rect.top) / scaleY - 3 : 'auto',
                left: (rect.left - slideRect.left) / scaleX,
                width: rect.width / scaleX,
                maxHeight,
            });
        };

        updateDropdownPosition();
        requestAnimationFrame(updateDropdownPosition);

        const frameWindow = anchorRef.current?.ownerDocument.defaultView;
        window.addEventListener('resize', updateDropdownPosition);
        window.addEventListener('scroll', updateDropdownPosition, true);
        if (frameWindow && frameWindow !== window) {
            frameWindow.addEventListener('resize', updateDropdownPosition);
            frameWindow.addEventListener('scroll', updateDropdownPosition, true);
        }

        return () => {
            window.removeEventListener('resize', updateDropdownPosition);
            window.removeEventListener('scroll', updateDropdownPosition, true);
            if (frameWindow && frameWindow !== window) {
                frameWindow.removeEventListener('resize', updateDropdownPosition);
                frameWindow.removeEventListener('scroll', updateDropdownPosition, true);
            }
        };
    }, [showComment, portalRoot, anchorRef, useFixedPosition, scale]);

    useEffect(() => {
        if (!showComment) {
            return (): void => undefined;
        }

        const handleClickOutside = (event: MouseEvent): void => {
            const path = event.composedPath();
            const isInsideAnchor = !!anchorRef.current && path.includes(anchorRef.current);
            const isInsideDropdown = !!dropdownRef.current && path.includes(dropdownRef.current);

            if (!isInsideAnchor && !isInsideDropdown) {
                setShowComment?.(false);
            }
        };

        const documents = new Set<Document>([document]);
        const ownerDocument = anchorRef.current?.ownerDocument;
        if (ownerDocument) documents.add(ownerDocument);

        documents.forEach((eventDocument) => eventDocument.addEventListener('mousedown', handleClickOutside));

        return () => {
            documents.forEach((eventDocument) => eventDocument.removeEventListener('mousedown', handleClickOutside));
        };
    }, [showComment, anchorRef, setShowComment]);

    return (
        <>
            <div
                className="c-questionElement__answer_comment"
                onClick={() => setShowComment?.(!showComment)}
                onMouseEnter={() => {
                    if (!showComment) {
                        setShowComment?.(true);
                        setHoverOpened(true);
                    }
                }}
                onMouseLeave={() => {
                    if (hoverOpened) {
                        setShowComment?.(false);
                        setHoverOpened(false);
                    }
                }}
            >
                <Icon icon="solar:chat-line-bold" />
            </div>
            {createPortal(
                <div
                    ref={dropdownRef}
                    className={`c-questionElement__answer_dropdown ${showComment ? 'is-visible' : ''} ${
                        dropdownPlacement === 'top' ? 'is-top' : ''
                    }`}
                    style={dropdownStyle}
                >
                    {comment}
                </div>,
                portalRoot
            )}
        </>
    );
}

export default AnswerComment;
